/* eslint-disable max-statements */
import { useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { debounce } from 'lodash-es';
import { type CSSProperties } from 'styled-components';
import { computePosition } from './utils/computePosition.js';
import type { DSHookFloatingContextT } from './react-desc-prop-types.js';
import type { PopoverArrowT } from './parts/PopoverArrow.js';

type UseComputedPositionStylesT = {
  /** Prevent computing when closed (optimization + avoids unnecessary frames) */
  preventComputing?: boolean;
  reference: Element | null;
  floating: HTMLElement | null;
  placement: DSHookFloatingContextT.PopperPlacementsT;
  placementOrderPreference?: DSHookFloatingContextT.PopperPlacementsT[];
  customOffset: [number, number];
  withoutPortal: boolean;
  /** Debounce ms for scroll/resize/observer events */
  debounceMs?: number;
};

export const useComputedPositionStyles = (config: UseComputedPositionStylesT) => {
  const {
    reference,
    floating,
    placement,
    placementOrderPreference,
    customOffset,
    withoutPortal,
    preventComputing = false,
    debounceMs = 150,
  } = config;

  const [arrowStyles, setArrowStyles] = useState<PopoverArrowT>({ style: { left: 0 }, placement: 'top' });

  // Important: do not initialize top/left to (0,0); keep hidden until the first computation
  const [floatingStyles, setFloatingStyles] = useState<CSSProperties>({
    position: 'absolute',
    zIndex: 3000,
    visibility: 'hidden',
    willChange: 'transform',
  });

  const [hasComputedOnce, setHasComputedOnce] = useState(false);

  const canCompute = reference !== null && floating !== null && !preventComputing;

  const updateStyles = useCallback(() => {
    if (!canCompute) return;

    const { coordsStyle, finalPlacement, coordsArrow } = computePosition({
      reference,
      floating,
      placement,
      placementOrderPreference,
      customOffset,
      withoutPortal,
    });

    // Do not touch visibility here; it is managed outside depending on open/hasComputedOnce
    setFloatingStyles((prev) => ({
      position: 'absolute',
      zIndex: 3000,
      ...prev,
      ...coordsStyle,
    }));
    setArrowStyles({ style: coordsArrow, placement: finalPlacement });
    setHasComputedOnce(true);
  }, [canCompute, reference, floating, placement, placementOrderPreference, customOffset, withoutPortal]);

  // Store latest update function in a ref to keep debounced stable
  const mutableUpdateStyles = useRef(updateStyles);
  mutableUpdateStyles.current = updateStyles;

  const debouncedUpdateStyles = useMemo(() => {
    const d = debounce(() => {
      mutableUpdateStyles.current();
    }, debounceMs);
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceMs]);

  // Clean up debounce on unmount
  useLayoutEffect(
    () => () => {
      debouncedUpdateStyles.cancel();
    },
    [debouncedUpdateStyles],
  );

  // Recalculate BEFORE paint when dependencies change
  useLayoutEffect(() => {
    if (canCompute) {
      mutableUpdateStyles.current();
    }
  }, [canCompute, reference, floating, placement, placementOrderPreference, customOffset, withoutPortal]);

  const forceUpdatePosition = useCallback(() => {
    mutableUpdateStyles.current();
  }, []);

  // Do not reset coordinates when closing; just hide
  const resetVisibilityOnly = useCallback(() => {
    setFloatingStyles((prev) => ({
      ...prev,
      visibility: 'hidden',
    }));
  }, []);

  return useMemo(
    () => ({
      arrowStyles,
      floatingStyles,
      hasComputedOnce,
      updateStyles: forceUpdatePosition,
      debouncedUpdateStyles,
      mutableUpdateStyles,
      resetVisibilityOnly,
    }),
    [arrowStyles, floatingStyles, hasComputedOnce, forceUpdatePosition, debouncedUpdateStyles, resetVisibilityOnly],
  );
};
